import { useState, type ReactNode } from 'react';
import { Link as RouterLink, useLocation } from 'react-router';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import { ChevronDown, History, LogOut, PlusCircle, ScrollText, Settings2, OctagonAlert, Building2 } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';
import { useRoles } from '../api/hooks';
import { red } from '../theme/theme';

type NavItem = { to: string; label: string; icon: typeof History; show: boolean; match: (p: string) => boolean };

export function AppShell({ children }: { children: ReactNode }) {
  const { logout, accountLabel } = useAuth();
  const { me, isOperator, isAdmin } = useRoles();
  const loc = useLocation();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const items: NavItem[] = [
    { to: '/batches/new', label: 'Neuer Batch', icon: PlusCircle, show: isOperator, match: (p) => p === '/batches/new' },
    { to: '/batches', label: 'Batch-Historie', icon: History, show: true, match: (p) => p.startsWith('/batches') && p !== '/batches/new' },
    { to: '/audit', label: 'Protokoll', icon: ScrollText, show: true, match: (p) => p.startsWith('/audit') },
    { to: '/admin', label: 'Administration', icon: Settings2, show: isAdmin, match: (p) => p.startsWith('/admin') },
  ];

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <a href="#main" className="visually-hidden" style={{ position: 'absolute' }}>
        Zum Inhalt springen
      </a>
      <AppBar position="sticky">
        <Toolbar sx={{ gap: 2, minHeight: { xs: 56, md: 64 }, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mr: 1 }}>
            <Box aria-hidden sx={{ width: 30, height: 30, borderRadius: '9px', backgroundColor: red[500], display: 'grid', placeItems: 'center' }}>
              <svg width="16" height="16" viewBox="0 0 32 32"><path d="M9 24 16 7l7 17h-4.4L16 17.4 13.4 24z" fill="#fff" /></svg>
            </Box>
            <Typography component="span" sx={{ fontFamily: "'Jost', sans-serif", fontWeight: 600, fontSize: '1.15rem', letterSpacing: '0.02em', whiteSpace: 'nowrap' }}>
              COTINGA <Box component="span" sx={{ color: red[500] }}>Autopilot</Box>
            </Typography>
          </Box>
          {me && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary', minWidth: 0 }} title={`Nidus-Instanz ${me.installation.nidusInstanceId}`}>
              <Building2 size={16} aria-hidden />
              <Typography variant="body2" noWrap data-testid="instance-name">
                {me.installation.name}
              </Typography>
            </Box>
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            color="inherit"
            onClick={(e) => setAnchor(e.currentTarget)}
            endIcon={<ChevronDown size={16} />}
            aria-haspopup="menu"
            aria-controls={anchor ? 'user-menu' : undefined}
            sx={{ fontWeight: 600, textAlign: 'left' }}
            data-testid="user-menu-button"
          >
            <Box component="span" sx={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
              <span>{me?.user.name ?? accountLabel ?? 'Angemeldet'}</span>
              {me && (
                <Typography component="span" variant="caption" sx={{ color: 'text.secondary' }}>
                  {me.roles.includes('Admin') ? 'Admin' : me.roles.includes('Operator') ? 'Operator' : 'Viewer'}
                </Typography>
              )}
            </Box>
          </Button>
          <Menu id="user-menu" anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}>
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="subtitle2">{me?.user.name}</Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {me?.user.email}
              </Typography>
              <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                Rollen: {me?.roles.join(', ') || '–'}
              </Typography>
            </Box>
            <Divider />
            <MenuItem
              onClick={() => {
                setAnchor(null);
                void logout();
              }}
            >
              <LogOut size={16} style={{ marginRight: 8 }} /> Abmelden
            </MenuItem>
          </Menu>
        </Toolbar>
        <Box component="nav" aria-label="Hauptnavigation" sx={{ borderTop: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
          <Container maxWidth={false} sx={{ display: 'flex', gap: 0.5, px: { xs: 1, md: 3 } }}>
            {items
              .filter((i) => i.show)
              .map((i) => {
                const active = i.match(loc.pathname);
                const Icon = i.icon;
                return (
                  <Button
                    key={i.to}
                    component={RouterLink}
                    to={i.to}
                    aria-current={active ? 'page' : undefined}
                    startIcon={<Icon size={17} />}
                    sx={{
                      borderRadius: 0,
                      py: 1.25,
                      px: 1.75,
                      color: active ? red[600] : 'text.secondary',
                      borderBottom: '3px solid',
                      borderColor: active ? red[500] : 'transparent',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                      '&:hover': { backgroundColor: red[50], color: red[700] },
                    }}
                  >
                    {i.label}
                  </Button>
                );
              })}
          </Container>
        </Box>
      </AppBar>
      {me && !me.executionEnabled && (
        <Alert severity="error" icon={<OctagonAlert size={20} />} sx={{ borderRadius: 0 }} role="status" data-testid="emergency-stop-banner">
          <strong>Globaler Not-Stopp aktiv:</strong> Es werden keine Antworten versendet und keine Tickets gelöst – weder manuell noch automatisch. Prüfung und Vorschau bleiben möglich.
        </Alert>
      )}
      <Container component="main" id="main" maxWidth={false} sx={{ flex: 1, py: { xs: 2, md: 4 }, px: { xs: 2, md: 3 }, maxWidth: 1560 }}>
        {children}
      </Container>
    </Box>
  );
}
